/**
 * The authenticated layout: one 232px sidebar, and no masthead.
 *
 * Everything navigable in this product is a server, so the sidebar *is* the
 * server list — a row per machine, with its state dot beside its hostname. That
 * is why there is no top navbar: an earlier iteration put the servers in a
 * horizontal masthead, where four machines fitted, six clipped, and the row
 * competed with the page heading directly underneath it. Do not reintroduce one.
 *
 * The list lives here rather than on the servers page because two surfaces need
 * it — the rail on every route, and the page on one of them — and two owners
 * would mean two requests and two polling loops. It is fetched once, published
 * through `useServers`, and polled only while a machine is mid-transition, which
 * is the same rule the servers page applied before the shell took it over.
 *
 * The page container is deliberately *not* here. The servers screen opens with a
 * full-bleed red attention band and the suspended screen is a full-bleed poster;
 * a max-width wrapper in the shell would box both of them in. Each screen sets
 * its own `max-w-content` column.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ApiError, api } from '../api/client';
import { isTransitional, type Instance, type InstanceListResponse } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { serverPath, serverTitle } from '../lib/servers';
import { storefrontOrigin } from '../lib/storefront';
import StatusBanner from './StatusBanner';
import { LogoMark, Skeleton, StatusDot, cx } from './ui';

/** Only the transitional states move on their own, so nothing else is polled. */
const POLL_MS = 5_000;

/* ------------------------------------------------------------ server list */

interface ServersState {
  /** Null until the first answer arrives — that is what a skeleton is for. */
  instances: Instance[] | null;
  /** The last failure. Never clears what is already on screen. */
  error: ApiError | null;
  loading: boolean;
  /** True while any machine is in a state it will leave on its own. */
  working: boolean;
  reload: () => void;
}

const ServersContext = createContext<ServersState | null>(null);

/**
 * The account's machines, as the shell already has them. A screen that needs the
 * list reads it from here; fetching `/api/v1/instances` a second time would put
 * two pollers on the same endpoint the moment something starts building.
 */
export function useServers(): ServersState {
  const ctx = useContext(ServersContext);
  if (!ctx) throw new Error('useServers must be used inside <AppShell>.');
  return ctx;
}

/** A superseded request under StrictMode's double mount is not a failure. */
function aborted(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'AbortError';
}

function useInstanceList(): ServersState {
  const [instances, setInstances] = useState<Instance[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const response = await api.get<InstanceListResponse>('/api/v1/instances', signal ? { signal } : {});
      if (signal?.aborted) return;
      setInstances(response.instances);
      setError(null);
    } catch (err) {
      if (aborted(err) || signal?.aborted) return;
      // What is already on screen stays: a poll that failed once has not made
      // the machines vanish, and blanking the rail would say that it had.
      setError(err instanceof ApiError ? err : new ApiError(0, 'error', 'Something went wrong.'));
    }
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    void load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  const working = instances?.some((instance) => isTransitional(instance.state)) ?? false;

  /**
   * The poll is conditional because a settled list cannot change without the
   * customer doing something, and this panel is left open on a second monitor
   * all day. Polling regardless would mean a request every five seconds, per
   * open tab, per customer, forever — each one a fan-out of instance, plan and
   * address reads — to re-render bytes that are already correct.
   */
  useEffect(() => {
    if (!working) return;
    const ctrl = new AbortController();
    const timer = window.setInterval(() => void load(ctrl.signal), POLL_MS);
    return () => {
      window.clearInterval(timer);
      ctrl.abort();
    };
  }, [working, load]);

  const reload = useCallback(() => void load(), [load]);

  return useMemo(
    () => ({ instances, error, loading: instances === null && error === null, working, reload }),
    [instances, error, working, reload],
  );
}

/* -------------------------------------------------------------- the sidebar */

function ArrowIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

/**
 * One machine. The active row is marked by a 2px accent bar down its leading
 * edge as well as by the fill, because on a 232px column of near-black a change
 * of background alone is easy to miss at a glance.
 */
function ServerRow({ instance, onNavigate }: { instance: Instance; onNavigate?: () => void }) {
  const location = useLocation();
  const prefix = `/servers/${encodeURIComponent(instance.id)}`;
  const on = location.pathname === prefix || location.pathname.startsWith(`${prefix}/`);
  return (
    <NavLink
      to={serverPath(instance)}
      onClick={onNavigate}
      className={() =>
        cx(
          // 40px, not the design's 36: this row is the whole navigation on a
          // phone, and the hit-target floor wins over four pixels of density.
          'flex h-10 items-center gap-2.5 border-l-2 px-2.5 transition-colors',
          on
            ? 'border-accent bg-ground-panel text-ink-primary'
            : 'border-transparent text-ink-secondary hover:bg-ground-panel/60 hover:text-ink-primary',
        )
      }
    >
      <StatusDot status={instance.state} size="sm" halo={false} />
      <span className="truncate font-mono text-sm font-medium">{serverTitle(instance)}</span>
    </NavLink>
  );
}

function ExternalIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 5h5v5M19 5l-8 8M18 14v4a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2h4" />
    </svg>
  );
}

/**
 * One link out of the panel.
 *
 * Marked as leaving, in two ways: an icon, and a label on the link itself for
 * anybody using a screen reader. A link that silently swaps hostname and design
 * is the kind of thing people report as being logged out.
 */
function SidebarLink({ href, label }: { href: string; label: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="flex h-9 items-center gap-2.5 px-2.5 text-xs font-medium text-ink-tertiary transition-colors hover:bg-ground-panel/60 hover:text-ink-primary"
      aria-label={`${label} (opens on the storefront in a new tab)`}
    >
      <span className="flex-1 truncate">{label}</span>
      <ExternalIcon className="h-3 w-3 shrink-0 opacity-60" />
    </a>
  );
}

function SidebarBody({ onNavigate }: { onNavigate?: () => void }) {
  const { user, logout } = useAuth();
  const { instances, error, loading } = useServers();

  const email = user?.email ?? '';
  const displayName = accountName(user?.displayName, email);
  const initials = initialsFor(user?.displayName, email);

  /*
   * Where this customer's billing lives.
   *
   * Nothing is sold or invoiced in this panel: the machine is bought on a
   * storefront, and every question about money, invoices, cancelling or buying
   * another one is answered over there. The rail had no way back, so the answer
   * to "where do I see my invoice" was to remember a hostname.
   *
   * Keyed on the provider the session was created with, because the two brands
   * are separate accounts and sending somebody to the wrong one shows them a
   * login screen for an account they do not have.
   */
  const storeOrigin = storefrontOrigin(user?.provider);

  return (
    <>
      <Link
        to="/"
        onClick={onNavigate}
        aria-label="VGNCloud home"
        className="flex items-center gap-[11px] px-[18px] pb-4 pt-5"
      >
        <LogoMark className="h-7 w-7" />
        <span className="text-[16px] font-extrabold tracking-display text-ink-primary">VGNCloud</span>
      </Link>

      <div className="flex items-center justify-between px-[18px] pb-2 pt-3.5">
        <span className="label-caps">Servers</span>
        <span className="font-mono text-xs font-medium tabular-nums text-ink-tertiary">
          {instances === null ? '—' : instances.length}
        </span>
      </div>

      <nav aria-label="Your servers" className="min-h-0 flex-1 overflow-y-auto px-3">
        {loading ? (
          <ul className="space-y-1 py-1">
            {[0, 1, 2].map((row) => (
              <li key={row} className="flex h-10 items-center gap-2.5 px-2.5">
                <Skeleton className="h-1.5 w-1.5" />
                <Skeleton className="h-3.5 w-24" />
              </li>
            ))}
          </ul>
        ) : null}

        {instances !== null
          ? instances.map((instance) => (
              <ServerRow key={instance.id} instance={instance} onNavigate={onNavigate} />
            ))
          : null}

        {instances !== null && instances.length === 0 ? (
          <p className="px-2.5 py-2 text-xs leading-relaxed text-ink-tertiary">
            Nothing set up on this account yet.
          </p>
        ) : null}

        {/* A rail that failed to load says so quietly. The page it frames has the
            room to explain it properly, and two copies of the same failure on
            one screen reads as two failures. */}
        {instances === null && error !== null ? (
          <p className="px-2.5 py-2 text-xs leading-relaxed text-ink-tertiary">Your servers could not be loaded.</p>
        ) : null}

        <Link
          to="/"
          onClick={onNavigate}
          className="flex h-10 items-center gap-2 px-2.5 text-xs font-semibold text-ink-tertiary hover:text-ink-primary"
        >
          View all
          <ArrowIcon className="h-[13px] w-[13px]" />
        </Link>
      </nav>

      {/* Everything that is not a machine. Kept below the server list and above
          the account, because it is a destination people look for once rather
          than navigate by. */}
      <div className="border-t border-rule-faint px-3 pb-1 pt-3">
        <p className="label-caps px-2.5 pb-1.5">Your account</p>
        <SidebarLink href={`${storeOrigin}/client`} label="Billing and invoices" />
        <SidebarLink href={`${storeOrigin}/vps/`} label="Buy another server" />
        <SidebarLink href={`${storeOrigin}/support`} label="Support" />
      </div>

      {/* The name first, the address under it.
          A truncated address on its own ("justus@worldistheli…") tells somebody
          nothing they did not already know and cuts off exactly where it would
          start to. The name is short enough to fit, and the address stays
          underneath for the case where one person holds two accounts. */}
      <div className="flex items-center gap-2.5 border-t border-rule-faint px-3.5 py-3.5">
        <span
          aria-hidden="true"
          className="flex h-7 w-7 shrink-0 items-center justify-center bg-rule-strong text-xs font-bold text-ink-mono"
        >
          {initials}
        </span>
        <span className="min-w-0 flex-1" title={email}>
          <span className="block truncate text-xs font-semibold text-ink-secondary">{displayName}</span>
          <span className="block truncate text-[11px] leading-tight text-ink-tertiary">{email}</span>
        </span>
        <button
          type="button"
          onClick={() => void logout()}
          aria-label="Sign out"
          title="Sign out"
          className="flex h-10 w-10 shrink-0 items-center justify-center text-ink-tertiary transition-colors hover:bg-ground-panel hover:text-ink-primary"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M15 4h3a2 2 0 012 2v12a2 2 0 01-2 2h-3M10 8l-4 4 4 4M6 12h9" />
          </svg>
        </button>
      </div>
    </>
  );
}

/**
 * What this account is called, in one word or two.
 *
 * The name the storefront holds, when there is one worth using. Some SSO
 * profiles carry the email address in the display-name field, and "justus@wor"
 * is not a name, so an address that arrives here is reduced to its local part
 * and tidied the same way a name is.
 */
export function accountName(name: string | null | undefined, email: string): string {
  const given = (name ?? '').trim();
  if (given !== '' && !given.includes('@')) return given;

  // Fall back to the address's local part, with the separators people put in
  // it read as spaces: justus.virtanen and justus-v both become two words.
  const local = (given.includes('@') ? given : email).split('@')[0] ?? '';
  const words = local
    .split(/[._\-+]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1));
  return words.join(' ');
}

/** Two letters from a name, and never an empty square. */
export function initialsFor(name: string | null | undefined, email: string): string {
  const words = accountName(name, email).split(/\s+/).filter(Boolean);
  const letters = words
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .filter((letter) => /[A-Z0-9ÅÄÖ]/.test(letter))
    .join('');
  return letters === '' ? '?' : letters;
}

/* ------------------------------------------------------------------- shell */

export default function AppShell({ children }: { children: ReactNode }) {
  const location = useLocation();
  const servers = useInstanceList();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Any navigation closes the drawer. Without this, tapping a server on a phone
  // leaves the overlay covering the page you just asked for.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // The drawer is a layer over the page, so the page behind it must not scroll.
  useEffect(() => {
    if (!drawerOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [drawerOpen]);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDrawerOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  return (
    <ServersContext.Provider value={servers}>
      <div className="flex min-h-screen">
        <aside className="sticky top-0 hidden h-screen w-sidebar shrink-0 flex-col border-r-2 border-rule-strong bg-ground-alt lg:flex">
          <SidebarBody />
        </aside>

        {drawerOpen ? (
          <div className="fixed inset-0 z-50 lg:hidden">
            <button
              type="button"
              aria-label="Close menu"
              className="absolute inset-0 bg-ground/80"
              onClick={() => setDrawerOpen(false)}
            />
            <div className="absolute inset-y-0 left-0 flex w-sidebar flex-col border-r-2 border-rule-strong bg-ground-alt">
              <SidebarBody onNavigate={() => setDrawerOpen(false)} />
            </div>
          </div>
        ) : null}

        <div className="flex min-w-0 flex-1 flex-col">
          {/* Below lg the sidebar is a drawer, so it needs one door. A slim bar,
              not a masthead: it carries the way in and the mark, and nothing
              else ever moves into it. */}
          <div className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b-2 border-rule-strong bg-ground/95 px-3 backdrop-blur lg:hidden">
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              aria-label="Open menu"
              aria-expanded={drawerOpen}
              className="flex h-10 w-10 shrink-0 items-center justify-center text-ink-secondary hover:text-ink-primary"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <path d="M4 7h16M4 12h16M4 17h16" />
              </svg>
            </button>
            <Link to="/" aria-label="VGNCloud home" className="flex items-center gap-2.5">
              <LogoMark className="h-7 w-7" />
              <span className="text-[15px] font-extrabold tracking-display text-ink-primary">VGNCloud</span>
            </Link>
          </div>

          {/* Keyed on the path so each navigation replays the rise rather than
              only the first one. Short and upward: enough to say "this is new",
              not enough to wait for during an incident. */}
          {/* Incident strip: under the phone bar, above every page, never in
              the sidebar. A Hetzner-style banner, not a masthead — do not
              hang navigation off it. */}
          <StatusBanner />

          <main key={location.pathname} className="animate-rise flex-1">
            {children}
          </main>
        </div>
      </div>
    </ServersContext.Provider>
  );
}
